import {Testimonial} from "../../../models/site";
import {useEffect, useState} from "react";
import {adminFetchTestimonial, fetchTestimonial} from "../../../components/business_logic/testimonial_requests";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Rating from "@mui/material/Rating";

const formatUserName = (user?: { first_name?: string; last_name?: string; username?: string; email?: string }): string => {
    if (!user) return 'User';
    
    if (user.first_name) {
        const lastNameInitial = user.last_name ? ` ${user.last_name.charAt(0).toUpperCase()}` : '';
        return `${user.first_name}${lastNameInitial}`;
    }
    
    // Fallback to username or email if first_name is not available
    return user.username || user.email || 'User';
};

interface PreviewTestimonialProps {
    testimonial: Testimonial;
    isAdmin: boolean;
}

export default function PreviewTestimonial({testimonial, isAdmin}: PreviewTestimonialProps) {
    const [fullTestimonial, setFullTestimonial] = useState({} as Testimonial);
    
    useEffect(() => {
        if (isAdmin) {
            adminFetchTestimonial(testimonial).then(t => {
                if (t && Object.keys(t).length > 0) {
                    setFullTestimonial(t);
                }
            })
        } else {
            fetchTestimonial(testimonial).then(t => {
                if (t && Object.keys(t).length > 0) {
                    setFullTestimonial(t);
                }
            })
        }
    }, [testimonial, isAdmin])

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return dateString;
        }
    };

    const displayTestimonial = fullTestimonial && Object.keys(fullTestimonial).length > 0 ? fullTestimonial : testimonial;

    return (
        <Container 
            id="testimonial-preview"
            sx={{
                pt: { xs: 4, sm: 6 },
                pb: { xs: 4, sm: 6 },
                px: { xs: 3, sm: 4 },
                maxWidth: '800px',
                margin: '0 auto'
            }}
        >
            {/* Header Section */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
                <Typography 
                    component="h1" 
                    variant="h3" 
                    gutterBottom 
                    sx={{ 
                        color: 'text.primary',
                        fontWeight: 600,
                        mb: 2
                    }}
                >
                    {displayTestimonial.title || 'Untitled Testimonial'}
                </Typography>

                <Divider sx={{ my: 3 }} />

                {/* Metadata */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        gap: 3,
                        flexWrap: 'wrap',
                        mb: 2
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        <strong>Created:</strong> {formatDate(displayTestimonial.created_at)}
                    </Typography>
                    {displayTestimonial.updated_at && displayTestimonial.updated_at !== displayTestimonial.created_at && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>Updated:</strong> {formatDate(displayTestimonial.updated_at)}
                        </Typography>
                    )}
                    {displayTestimonial.user && !displayTestimonial.is_anonymous && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>User:</strong> {formatUserName(displayTestimonial.user)}
                        </Typography>
                    )}
                    {displayTestimonial.is_anonymous && (
                        <Typography variant="body2" color="text.secondary">
                            <strong>Anonymous</strong>
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Rating Section */}
            {displayTestimonial.rating && (
                <Box 
                    sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                        p: { xs: 3, sm: 4 },
                        border: '1px solid',
                        borderColor: 'divider',
                        mb: 3,
                        textAlign: 'center'
                    }}
                >
                    <Typography 
                        variant="h6" 
                        gutterBottom 
                        sx={{ 
                            color: 'text.primary',
                            fontWeight: 600,
                            mb: 2
                        }}
                    >
                        Rating
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                        <Rating 
                            value={displayTestimonial.rating} 
                            readOnly 
                            size="large"
                            sx={{
                                '& .MuiRating-iconFilled': {
                                    color: 'primary.main'
                                }
                            }}
                        />
                        <Typography variant="h6" color="text.primary">
                            {displayTestimonial.rating} / 5
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Content Section */}
            <Box 
                sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    p: { xs: 3, sm: 4 },
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                        color: 'text.primary',
                        fontWeight: 600,
                        mb: 2
                    }}
                >
                    Testimonial
                </Typography>
                <Divider sx={{ mb: 3 }} />
                {displayTestimonial.content ? (
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            color: 'text.primary',
                            lineHeight: 1.7,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}
                    >
                        {displayTestimonial.content}
                    </Typography>
                ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        Loading testimonial content...
                    </Typography>
                )}
            </Box>
        </Container>
    );
}